import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Coins, ChevronDown, Loader2, AlertCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatCompactNumber, getTokenThresholdColor } from '@/lib/utils'
import type { TokenUsage } from '@/types/chatbot'

export interface SessionTokenSummaryProps {
  /** Token usage data */
  tokenUsage?: TokenUsage | null
  /** Whether token usage is loading */
  isLoading?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * SessionTokenSummary displays token usage for a single session.
 * Shows a collapsible summary with total tokens, cost, and model breakdown.
 * Designed for use in sidebars and compact layouts.
 *
 * @example
 * ```tsx
 * <SessionTokenSummary
 *   tokenUsage={tokenUsage}
 *   isLoading={isLoadingTokens}
 * />
 * ```
 */
export function SessionTokenSummary({
  tokenUsage,
  isLoading,
  className
}: SessionTokenSummaryProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)} data-testid="token-usage-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading tokens...</span>
      </div>
    )
  }

  if (!tokenUsage || tokenUsage.totalTokens === 0) {
    return null
  }

  return (
    <Collapsible defaultOpen={false} data-testid="session-token-usage" className={className}>
      <CollapsibleTrigger className="flex items-center justify-between w-full group">
        <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Coins className="h-4 w-4" />
          <span>Tokens</span>
        </h3>
        <div className="flex items-center gap-1">
          <span className={cn('text-sm font-bold', getTokenThresholdColor(tokenUsage.totalTokens))}>
            {formatCompactNumber(tokenUsage.totalTokens)}
          </span>
          {tokenUsage.estimatedCost !== null && tokenUsage.estimatedCost > 0 && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              ${tokenUsage.estimatedCost.toFixed(2)}
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 space-y-2 text-xs">
          {/* Input/Output Summary */}
          <div className="flex justify-between text-muted-foreground">
            <span>In: {formatCompactNumber(tokenUsage.totalInputTokens)}</span>
            <span>Out: {formatCompactNumber(tokenUsage.totalOutputTokens)}</span>
          </div>
          
          {/* Model Breakdown */}
          {tokenUsage.modelBreakdown && tokenUsage.modelBreakdown.length > 0 && (
            <TooltipProvider>
              <div className="space-y-1 pt-1 border-t border-border">
                <span className="text-muted-foreground/70 uppercase tracking-wide" style={{ fontSize: '10px' }}>By Model</span>
                {tokenUsage.modelBreakdown.map((model) => {
                  const costData = tokenUsage.costByModel?.find(c => c.modelName === model.modelName)
                  const hasPricing = !tokenUsage.modelsWithoutPricing?.includes(model.modelName)
                  return (
                    <div key={model.modelName} className="flex items-center justify-between">
                      <span className="truncate max-w-[100px] flex items-center gap-1" title={model.modelName}>
                        {model.modelName}
                        {!hasPricing && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertCircle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>No pricing configured for this model</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <span>{formatCompactNumber(model.inputTokens)}/{formatCompactNumber(model.outputTokens)}</span>
                        {costData && costData.totalCost > 0 && (
                          <span className="text-emerald-600 dark:text-emerald-400">${costData.totalCost.toFixed(2)}</span>
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </TooltipProvider>
          )}

          {/* Models without pricing warning */}
          {tokenUsage.modelsWithoutPricing && tokenUsage.modelsWithoutPricing.length > 0 && (
            <div className="text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded">
              <span className="font-medium">No pricing: </span>
              {tokenUsage.modelsWithoutPricing.join(', ')}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
